import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DIMENSOES } from "@/lib/constants";
import { ArrowLeft, Loader2, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { toast } from "sonner";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

export default function FormativaModelo() {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [cursoId, setCursoId] = useState("");
  const [nome, setNome] = useState("Avaliação Formativa");
  const [criterios, setCriterios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/cursos").then((r) => setCursos(r.data)); }, []);

  const loadModelo = (cid) => {
    setCursoId(cid);
    setLoading(true);
    api.get(`/formativa/modelo?curso_id=${cid}`).then((r) => {
      const m = r.data;
      if (m) {
        setNome(m.nome || "Avaliação Formativa");
        setCriterios((m.criterios || []).map((c) => ({ ...c })));
      } else {
        setNome("Avaliação Formativa");
        setCriterios([]);
      }
    }).finally(() => setLoading(false));
  };

  const addCrit = () =>
    setCriterios((cs) => [...cs, { id: uid(), nome: "", dimensao: "atitude", ordem: cs.length }]);
  const updateCrit = (i, patch) =>
    setCriterios((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeCrit = (i) => setCriterios((cs) => cs.filter((_, idx) => idx !== i));
  const move = (i, dir) => setCriterios((cs) => {
    const j = i + dir;
    if (j < 0 || j >= cs.length) return cs;
    const arr = [...cs];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });

  const save = async () => {
    if (!cursoId) { toast.error("Selecione um curso"); return; }
    if (!criterios.length || criterios.some((c) => !c.nome.trim())) {
      toast.error("Preencha o nome de todos os critérios"); return;
    }
    setSaving(true);
    try {
      await api.put("/formativa/modelo", {
        curso_id: cursoId,
        nome,
        criterios: criterios.map((c, i) => ({ id: c.id, nome: c.nome.trim(), dimensao: c.dimensao, ordem: i })),
      });
      toast.success("Modelo base salvo");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <button data-testid="fmodelo-voltar" onClick={() => navigate("/formativa")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <PageHeader title="Modelo base — Formativa" subtitle="Critérios padrão (escala 0-10) usados na Avaliação Formativa deste curso" />

      <Card className="p-5 bg-card border-border mb-5 space-y-4">
        <div>
          <label className="text-xs text-muted-foreground">Curso</label>
          <Select value={cursoId} onValueChange={loadModelo}>
            <SelectTrigger data-testid="fmodelo-curso" className="w-full sm:w-[320px]"><SelectValue placeholder="Selecione o curso" /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {cursos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {cursoId && (
          <div>
            <label className="text-xs text-muted-foreground">Nome do modelo</label>
            <Input data-testid="fmodelo-nome" value={nome} onChange={(e) => setNome(e.target.value)} className="w-full sm:w-[420px]" />
          </div>
        )}
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : cursoId ? (
        <>
          <div className="space-y-3 mb-5">
            {criterios.map((c, i) => (
              <Card key={c.id} data-testid={`fmodelo-crit-${i}`} className="p-3 bg-card border-border">
                <div className="flex items-center gap-2">
                  <span className="w-6 text-center text-xs text-muted-foreground font-mono">{i + 1}</span>
                  <Input data-testid={`fmodelo-crit-nome-${i}`} value={c.nome} placeholder="Nome do critério"
                    onChange={(e) => updateCrit(i, { nome: e.target.value })} className="flex-1" />
                  <Select value={c.dimensao} onValueChange={(v) => updateCrit(i, { dimensao: v })}>
                    <SelectTrigger data-testid={`fmodelo-crit-dim-${i}`} className="w-[150px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      {Object.entries(DIMENSOES).map(([k, d]) => <SelectItem key={k} value={k}>{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Badge className={`${DIMENSOES[c.dimensao]?.badge} hidden lg:inline-flex text-[10px]`}>{DIMENSOES[c.dimensao]?.label}</Badge>
                  <button data-testid={`fmodelo-up-${i}`} onClick={() => move(i, -1)} className="p-1.5 text-muted-foreground hover:text-foreground"><ArrowUp className="h-4 w-4" /></button>
                  <button data-testid={`fmodelo-down-${i}`} onClick={() => move(i, 1)} className="p-1.5 text-muted-foreground hover:text-foreground"><ArrowDown className="h-4 w-4" /></button>
                  <button data-testid={`fmodelo-remove-${i}`} onClick={() => removeCrit(i)} className="p-1.5 text-muted-foreground hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              </Card>
            ))}
            {!criterios.length && <p className="text-sm text-muted-foreground">Nenhum critério. Adicione abaixo.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" data-testid="fmodelo-add" onClick={addCrit}>
              <Plus className="h-4 w-4 mr-2" /> Adicionar critério
            </Button>
            <Button data-testid="fmodelo-salvar" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salvar modelo
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Selecione um curso para editar o modelo base.</p>
      )}
    </div>
  );
}
